import { useEffect, useState, type FormEvent } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { extractErrorMessage } from '../../api/error-message';

// Shared TOTP enrolment panel, used in two places:
//   * the login page, right after the password step, when the API answers
//     `enrolmentRequired: true` — bearer is the enrolment challenge token and a
//     successful confirm returns the admin's FIRST session (2FA is mandatory,
//     so there is no session before this);
//   * the signed-in /2fa-setup page, to re-enrol — bearer is the access token,
//     and since the account already has a factor the API also wants a code from
//     the CURRENT authenticator (`reenrolment: true`).
//
// Calls the API with its own axios instance (like services/auth.ts) so these
// requests bypass the refresh-on-401 interceptor: a 401 here means "wrong code"
// or "enrolment step expired", never "refresh your session".

const baseURL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';
const client = axios.create({ baseURL, withCredentials: true, timeout: 15_000 });

interface SetupResponse {
  secret: string;
  otpauthUrl: string;
  /** True when the account already has 2FA on (re-enrolment). */
  reenrolment?: boolean;
}

/** Tokens returned by /2fa/enable when called with an enrolment token. */
export interface EnrolmentTokens {
  accessToken: string;
  refreshToken: string;
  expiresInSec: number;
  actorType: 'ADMIN';
  role: string;
  scope: string[];
}

interface Props {
  /** Enrolment challenge token (login) or access token (re-enrolment). */
  bearer: string;
  /** Called once the new factor is confirmed. `tokens` is set only for first enrolment. */
  onEnabled: (tokens: EnrolmentTokens | null) => void;
  submitLabel?: string;
}

type Step = 'loading' | 'ready' | 'verifying' | 'error';

export function TotpEnrolment({ bearer, onEnabled, submitLabel = 'Enable 2FA' }: Props) {
  const [step, setStep] = useState<Step>('loading');
  const [setup, setSetup] = useState<SetupResponse | null>(null);
  const [code, setCode] = useState('');
  const [currentCode, setCurrentCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    client
      .post<SetupResponse>('/auth/admin/2fa/setup', {}, { headers: { Authorization: `Bearer ${bearer}` } })
      .then(({ data }) => {
        if (!cancelled) {
          setSetup(data);
          setStep('ready');
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(extractErrorMessage(err));
          setStep('error');
        }
      });
    return () => {
      cancelled = true;
    };
    // Once per mount: a background token refresh changes `bearer`, and
    // re-running setup would swap the QR out from under someone mid-scan.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const needsCurrent = Boolean(setup?.reenrolment);

  const onVerify = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStep('verifying');
    setError(null);
    try {
      const res = await client.post<EnrolmentTokens | ''>(
        '/auth/admin/2fa/enable',
        needsCurrent ? { totp: code, currentTotp: currentCode } : { totp: code },
        { headers: { Authorization: `Bearer ${bearer}` } },
      );
      // 200 + tokens for first enrolment, 204 (empty) for re-enrolment.
      onEnabled(res.status === 200 && res.data ? res.data : null);
    } catch (err) {
      setError(extractErrorMessage(err));
      setStep('ready');
    }
  };

  if (step === 'loading') {
    return <div className="panel p-8 text-[14px] text-ink-500">Provisioning a secret…</div>;
  }

  if (step === 'error' || !setup) {
    return (
      <div role="alert" className="alert alert-danger">
        <div>
          <p className="font-semibold">Setup failed</p>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // Letter-spaced tabular digits make a 6-digit code easy to read back.
  const inputClass = 'input font-tabular tracking-widest';

  return (
    <div className="panel grid grid-cols-1 gap-6 p-6">
      <div className="flex items-start justify-center">
        <div className="rounded-md border border-surface-line bg-white p-3">
          <QRCodeSVG value={setup.otpauthUrl} size={192} level="M" includeMargin={false} />
        </div>
      </div>

      <div className="min-w-0">
        <div className="field-label mb-1.5">
          Or enter this secret manually
        </div>
        <code className="sku mb-6 block break-all rounded-sm border border-surface-line bg-surface-sunk px-3 py-2.5 text-ink-900">
          {setup.secret}
        </code>

        <form onSubmit={onVerify} noValidate className="space-y-4">
          {needsCurrent && (
            <label htmlFor="current-totp" className="block">
              <span className="field-label mb-1.5">
                Code from your current authenticator
              </span>
              <input
                id="current-totp"
                name="current-totp"
                inputMode="numeric"
                pattern="\d{6}"
                autoComplete="one-time-code"
                value={currentCode}
                onChange={(e) => setCurrentCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className={inputClass}
                required
              />
            </label>
          )}
          <label htmlFor="totp" className="block">
            <span className="field-label mb-1.5">
              {needsCurrent ? 'Code from the new authenticator entry' : '6-digit code from app'}
            </span>
            <input
              id="totp"
              name="totp"
              inputMode="numeric"
              pattern="\d{6}"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className={inputClass}
              required
              autoFocus={!needsCurrent}
            />
          </label>

          {error && (
            <div
              role="alert"
              className="alert alert-danger"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={code.length !== 6 || (needsCurrent && currentCode.length !== 6) || step === 'verifying'}
            className="btn btn-primary btn-lg btn-block"
          >
            {step === 'verifying' ? 'Verifying…' : submitLabel}
          </button>
        </form>
      </div>
    </div>
  );
}
